import classNames from 'classnames';

import type { PayflowInitializer } from '@ctct/application-layer';
import { BillingStrategy, createResolvedBillingStrategy } from '@ctct/domain-layer';
import { AccountModificationFlowId } from '@ctct/infrastructure-layer';
import { Card } from '@ctct/payflow-ui';
import { BREAKPOINT_MINS } from '@ctct/presentation-layer';
import {
  AlertWidget,
  BillingInfoFormWidget,
  OrderSummaryWidget,
  PaymentButtonWidget,
  PaymentMethodsWidget,
  PricingConfiguratorWidget,
  PromoCodesWidget,
  ContactsTierSelectWidget,
  CurrencyWidget,
  NonProfitWidget,
  SmsBundleTierSelectWidget,
  SmsBundleTierModalWidget,
  useAddOnsSubscriptionWidget,
  useAddOnsWidget,
  useIsMobile,
  usePaymentButtonWidget,
  useCurrencyWidget,
  useNonProfitWidget,
  usePricingConfiguratorWidget,
  PaymentProcessingInfoWidget,
  usePaymentProcessingInfoWidget,
  useAlertWidget,
  usePaymentMethodsWidget,
  useContactsTierSelectWidget,
  usePrepaySelectWidget,
  useOrderSummaryWidget,
  usePromoCodesWidget,
  useEmailPlansWidget,
  PayflowProgressBarWidget,
  usePayflowProgressBarWidget,
  useRouterNonUiWidget,
  useInboundPromoCodesWidget,
  isApplePayParamEnabled,
  useBillingInfoFormWidget,
  isStripeParamEnabled,
  EmailPlansWidget,
  isStripePhase2ParamEnabled,
  PrepaySelectWidget,
  useIsSmsBundle,
  usePricingConfiguratorVisibility,
  useHideAddons,
} from '@ctct/react-app-layer';
import {
  createNonProfitHooks,
  createCurrencyHooks,
  usePayflowPageStore,
  createPaymentProcessingInfoHooks,
  createPaymentButtonHooks,
  createAlertHooks,
  createPaymentMethodsHooks,
  createPrepaySelectHooks,
  createContactsTierSelectHooks,
  createPromoCodesHooks,
  createOrderSummaryHooks,
  createEmailPlansHooks,
  createAddOnsHooks,
  createPayflowProgressBarHooks,
  createBillingInfoFormHooks,
} from '@ctct/state-client-layer';
import {
  useHasCharmPricing,
  useIs3PLCustomer,
  useIsCanceled,
  useIsSuspendedFacade,
  useRemove6MoPrepayFacade,
  usePaymentStrategy,
} from '@ctct/state-server-layer';

const nonProfitHooks = createNonProfitHooks(usePayflowPageStore);
const currencyHooks = createCurrencyHooks(usePayflowPageStore);
const paymentProcessingInfoHooks = createPaymentProcessingInfoHooks(usePayflowPageStore);
const paymentButtonHooks = createPaymentButtonHooks(usePayflowPageStore);
const alertHooks = createAlertHooks(usePayflowPageStore);
const paymentMethodsHooks = createPaymentMethodsHooks(usePayflowPageStore);
const prepaySelectHooks = createPrepaySelectHooks(usePayflowPageStore);
const contactsTierSelectHooks = createContactsTierSelectHooks(usePayflowPageStore);
const promoCodesHooks = createPromoCodesHooks(usePayflowPageStore);
const orderSummaryHooks = createOrderSummaryHooks(usePayflowPageStore);
const emailPlansHooks = createEmailPlansHooks(usePayflowPageStore);
const addOnsHooks = createAddOnsHooks(usePayflowPageStore);
const payflowProgressBarHooks = createPayflowProgressBarHooks(usePayflowPageStore);
const billingInfoFormHooks = createBillingInfoFormHooks(usePayflowPageStore);

// external hooks
const emailPlansExternalHooks = {
  useContactsTier: contactsTierSelectHooks.useContactsTier,
  usePrepay: prepaySelectHooks.usePrepay,
  useSmsBundleTier: addOnsHooks.useSelectedSmsBundleTier,
};
const orderSummaryExternalHooks = {
  useSelectedEmailPlanId: emailPlansHooks.useSelectedEmailPlanId,
  useContactsTier: contactsTierSelectHooks.useContactsTier,
  usePrepay: prepaySelectHooks.usePrepay,
  useSelectedAddOns: addOnsHooks.useSelectedAddOns,
  useHasPendingExternalRedirect: paymentMethodsHooks.useHasPendingExternalRedirect,
  useIsPromoValidationPending: promoCodesHooks.usePromoCodeValidationPending,
};
const addOnsExternalHooks = {
  useSelectedEmailPlanId: emailPlansHooks.useSelectedEmailPlanId,
  usePrepay: prepaySelectHooks.usePrepay,
};
const promoCodesExternalHooks = {
  useRatingCallPayload: orderSummaryHooks.useRatingCallPayload,
  useSelectedEmailPlanId: emailPlansHooks.useSelectedEmailPlanId,
  usePrepay: prepaySelectHooks.usePrepay,
};
const paymentButtonExternalHooks = {
  usePrepay: prepaySelectHooks.usePrepay,
  useHasPendingExternalRedirect: paymentMethodsHooks.useHasPendingExternalRedirect,
};
const paymentMethodsExternalHooks = {
  useRatingCallPayload: orderSummaryHooks.useRatingCallPayload,
};

const routerNonUiExternalHooks = {
  useIsHidePromoCodesEnabled: promoCodesHooks.useIsHidePromoCodesEnabled,
};

interface Props {
  initializer: PayflowInitializer;
  flowId: AccountModificationFlowId;
}

export const CheckoutPage = ({ initializer, flowId }: Props) => {
  const isSecureCheckout = true;
  const isSmsBundle = useIsSmsBundle();

  const isApplePayEnabled = isApplePayParamEnabled();
  const isStripeEnabled = isStripeParamEnabled() ?? false;
  const isStripePhase2Enabled = isStripePhase2ParamEnabled();

  const billingStrategy = createResolvedBillingStrategy(
    isStripeEnabled ? BillingStrategy.Stripe : BillingStrategy.InHouse
  );
  const paymentStrategy = usePaymentStrategy({
    billingStrategyQuery: billingStrategy,
  });

  const { is3PLCustomer: isCustomer, isTrialer } = useIs3PLCustomer();
  const hasCharmPricing = useHasCharmPricing();
  const isCanceled = useIsCanceled();
  const { data: isSuspendedData, isLoading: isLoadingIsSuspended } = useIsSuspendedFacade();
  const isSuspended = !isLoadingIsSuspended && isSuspendedData;
  const { isMobile, isMobileLoading } = useIsMobile(BREAKPOINT_MINS.mdWide);
  const isDesktop = !isMobileLoading && !isMobile;
  const hideAddons = useHideAddons();

  const remove6MoPrepayExperiment = useRemove6MoPrepayFacade({ isMobile, isMobileLoading });
  const isRemove6MoPrepayEnabled = remove6MoPrepayExperiment.data.isEnabled;

  const {
    shouldHidePricingConfigurator,
    shouldHideCurrencyWidget,
    shouldHideContactsTierSelectWidget,
    shouldHideNonProfitWidget,
    shouldHidePrepaySelectWidget,
    shouldHideSmsBundleTierSelectWidget,
  } = usePricingConfiguratorVisibility({ isCheckoutPage: true });

  useAddOnsSubscriptionWidget({
    initializer,
    billingStrategy,
  });
  useInboundPromoCodesWidget({ initializer, pageStore: usePayflowPageStore });

  const emailPlansWidgetProps = useEmailPlansWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: emailPlansHooks,
    externalHooks: emailPlansExternalHooks,
    billingStrategy,
    paymentStrategy,
    flags: { isSmsBundle },
  });

  const contactsTierSelectWidgetProps = useContactsTierSelectWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: contactsTierSelectHooks,
    billingStrategy,
    flags: { isSmsBundle },
  });

  const prepaySelectWidgetProps = usePrepaySelectWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: prepaySelectHooks,
    flags: {
      billingStrategy,
      isSuspended,
      disallowSelectionOfShorterPaymentPlan: isStripePhase2Enabled ?? false,
      skipInitializationFromUrl: false,
      isCustomer,
      isTrialer,
      hasCharmPricing,
      isRemove6MoPrepayEnabled,
    },
    flowId,
  });

  const orderSummaryWidgetProps = useOrderSummaryWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: orderSummaryHooks,
    externalHooks: orderSummaryExternalHooks,
    billingStrategy,
    flowId,
    flags: { isCanceled, isSmsBundle, isMobile },
  });

  const promoCodesWidgetProps = usePromoCodesWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: promoCodesHooks,
    externalHooks: promoCodesExternalHooks,
    billingStrategy,
    isCompact: true,
    hideButtonWhenInputIsEmpty: true,
  });

  const currencyWidgetProps = useCurrencyWidget({
    initializer,
    pageStore: usePayflowPageStore,
    flags: [isCustomer],
    hooks: currencyHooks,
    billingStrategy,
  });
  const nonProfitWidgetProps = useNonProfitWidget({
    initializer,
    pageStore: usePayflowPageStore,
    flags: [isCustomer],
    hooks: nonProfitHooks,
    billingStrategy,
  });
  const paymentProcessingInfoWidgetProps = usePaymentProcessingInfoWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: paymentProcessingInfoHooks,
  });

  const paymentMethodsWidgetProps = usePaymentMethodsWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: paymentMethodsHooks,
    isApplePayEnabled,
    isNewPayflow: true,
    isNewCustomerPayflow: isCustomer,
    billingStrategy,
    paymentStrategy,
    externalHooks: paymentMethodsExternalHooks,
  });

  const addOnsWidgetProps = useAddOnsWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: addOnsHooks,
    externalHooks: addOnsExternalHooks,
    billingStrategy,
    isSmsBundle,
  });

  useRouterNonUiWidget({
    initializer,
    isApplePayEnabled,
    isStripeEnabled,
    isStripePhase2Enabled: isStripePhase2Enabled ?? false,
    externalHooks: routerNonUiExternalHooks,
  });

  const promoCodesWidget =
    flowId === AccountModificationFlowId.ChangePaymentPlan || promoCodesWidgetProps.data.shouldHide() ? null : (
      <PromoCodesWidget {...promoCodesWidgetProps} />
    );
  const contactsTierSelectWidget = <ContactsTierSelectWidget {...contactsTierSelectWidgetProps} />;
  const currencyWidget = <CurrencyWidget {...currencyWidgetProps} />;
  const nonProfitWidget = <NonProfitWidget {...nonProfitWidgetProps} />;
  const prepaySelectWidget = <PrepaySelectWidget {...prepaySelectWidgetProps} />;
  const smsBundleTierSelectWidget = <SmsBundleTierSelectWidget {...addOnsWidgetProps} />;

  const paymentButtonWidgetProps = usePaymentButtonWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: paymentButtonHooks,
    externalHooks: paymentButtonExternalHooks,
    version: 'split-terms-text',
    billingStrategy,
    isCustomer,
  });
  const paymentButtonWidget = <PaymentButtonWidget {...paymentButtonWidgetProps} />;

  const alertWidgetProps = useAlertWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: alertHooks,
  });

  const payflowProgressBarWidgetProps = usePayflowProgressBarWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: payflowProgressBarHooks,
    flags: {
      isSecureCheckout,
      isCustomerFlow: isCustomer,
      isCanceled,
      hideAddons,
      isSuspended,
      isSmsBundle,
    },
    flowId,
    billingStrategy,
  });

  const pricingConfiguratorProps = usePricingConfiguratorWidget({
    initializer,
  });

  const billingInfoFormWidgetProps = useBillingInfoFormWidget({
    initializer,
    pageStore: usePayflowPageStore,
    hooks: billingInfoFormHooks,
    billingStrategy,
  });

  return (
    <>
      <PayflowProgressBarWidget {...payflowProgressBarWidgetProps} />
      <div className="payflow-v2">
        <div className="payflow-v2__alerts-wrapper">
          <AlertWidget {...alertWidgetProps} />
        </div>

        <div className="payflow-v2__top-wrapper">
          <PaymentProcessingInfoWidget {...paymentProcessingInfoWidgetProps} />
          {!shouldHidePricingConfigurator && isTrialer !== undefined && (
            <PricingConfiguratorWidget
              {...pricingConfiguratorProps}
              prepaySelectWidget={shouldHidePrepaySelectWidget ? null : prepaySelectWidget}
              contactsTierSelectWidget={shouldHideContactsTierSelectWidget ? null : contactsTierSelectWidget}
              smsBundleTierSelectWidget={shouldHideSmsBundleTierSelectWidget ? null : smsBundleTierSelectWidget}
              currencyWidget={shouldHideCurrencyWidget ? null : currencyWidget}
              nonProfitWidget={shouldHideNonProfitWidget ? null : nonProfitWidget}
            />
          )}
        </div>
        <div className="payflow-v2__bottom-wrapper">
          {isMobile && (
            <Card>
              <OrderSummaryWidget {...orderSummaryWidgetProps} injectedMiddleComponent={promoCodesWidget} />
            </Card>
          )}

          <Card className={classNames('payflow-v2__left-wrapper', 'max-width-right-520px')}>
            <PaymentMethodsWidget {...paymentMethodsWidgetProps} />
            <BillingInfoFormWidget {...billingInfoFormWidgetProps} />
          </Card>

          {isMobile && <Card>{paymentButtonWidget}</Card>}

          {isDesktop && (
            <Card className={classNames('payflow-v2__right-wrapper', 'max-width-520px')}>
              <OrderSummaryWidget {...orderSummaryWidgetProps} injectedMiddleComponent={promoCodesWidget} />
              {paymentButtonWidget}
            </Card>
          )}
        </div>
      </div>
      <EmailPlansWidget {...emailPlansWidgetProps} pricingTierDialogOnly />
      {isSmsBundle && isMobile && <SmsBundleTierModalWidget {...addOnsWidgetProps} />}
    </>
  );
};
